import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { formatPriceChange } from '@/data/mockData';
import { VendorPriceComparison } from '@/data/mockData';
import { TrendingUp, TrendingDown, Minus, Clock, Package, Star } from 'lucide-react';

interface VendorPricingComparisonProps {
  comparisons: VendorPriceComparison[];
  skuName: string;
  quantity: number;
  onVendorSelect?: (vendorId: number) => void;
}

export const VendorPricingComparison: React.FC<VendorPricingComparisonProps> = ({
  comparisons,
  skuName,
  quantity,
  onVendorSelect
}) => {
  if (comparisons.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Vendor Pricing Comparison</CardTitle>
          <CardDescription>No vendor pricing data available for {skuName}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No vendors found for this product</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Vendor Pricing Comparison</CardTitle>
        <CardDescription>
          Compare pricing for {skuName} (Qty: {quantity.toLocaleString()})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {comparisons.map((comparison, index) => {
            const priceChangeInfo = formatPriceChange(
              comparison.priceChange,
              comparison.priceChangePercent,
              comparison.previousPrice
            );

            return (
              <div
                key={comparison.vendorId}
                className={`p-4 border rounded-lg transition-all hover:shadow-sm ${
                  comparison.isPreferred ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{comparison.vendorName}</h3>
                      {comparison.isPreferred && (
                        <Badge variant="default" className="text-xs">
                          <Star className="h-3 w-3 mr-1" />
                          Preferred
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        Rating: {comparison.performanceRating.toFixed(1)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Current Price</p>
                        <p className="font-semibold text-lg">
                          {formatCurrency(comparison.currentPrice)}
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Previous Price</p>
                        <p className="font-medium">
                          {comparison.previousPrice 
                            ? formatCurrency(comparison.previousPrice)
                            : <span className="text-muted-foreground">No data</span>
                          }
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Price Change</p>
                        <div className="flex items-center gap-1">
                          {priceChangeInfo.icon}
                          <span className={`font-medium ${priceChangeInfo.color}`}>
                            {priceChangeInfo.text}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Total Cost</p>
                        <p className="font-semibold text-lg">
                          {formatCurrency(comparison.totalCost)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Lead Time:</span>
                        <span className="font-medium">{comparison.leadTime} days</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Min. Order:</span>
                        <span className="font-medium">{comparison.minimumOrderQty.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">Last Updated:</span>
                        <span className="font-medium">
                          {new Date(comparison.lastUpdated).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Price History Summary */}
                    {comparison.priceHistory.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Recent Price History</p>
                        <div className="space-y-1">
                          {comparison.priceHistory.slice(0, 3).map((entry, idx) => (
                            <div key={entry.id} className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">
                                {new Date(entry.date).toLocaleDateString()}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{formatCurrency(entry.price)}</span>
                                {entry.reason && (
                                  <span className="text-muted-foreground">({entry.reason})</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {onVendorSelect && (
                    <div className="ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onVendorSelect(comparison.vendorId)}
                        className="whitespace-nowrap"
                      >
                        Select Vendor
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-semibold mb-2">Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Cheapest Option</p>
              <p className="font-semibold">
                {comparisons[0]?.vendorName} - {formatCurrency(comparisons[0]?.totalCost || 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Most Expensive</p>
              <p className="font-semibold">
                {comparisons[comparisons.length - 1]?.vendorName} - {formatCurrency(comparisons[comparisons.length - 1]?.totalCost || 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Price Range</p>
              <p className="font-semibold">
                {formatCurrency((comparisons[comparisons.length - 1]?.totalCost || 0) - (comparisons[0]?.totalCost || 0))}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Vendors Available</p>
              <p className="font-semibold">{comparisons.length}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
